<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <employee-data-layout :employee-id="empNumber" screen="personal">
    <div class="orangehrm-card--details-container">
      <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('general.personal_details') }}
        </oxd-text>
        <oxd-divider />
        <oxd-form :loading="isLoading" @submit-valid="onSave">
          <oxd-form-row>
            <oxd-grid :cols="1" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <full-name-input
                  v-model:firstName="employee.firstName"
                  v-model:middleName="employee.middleName"
                  v-model:lastName="employee.lastName"
                  :rules="rules"
                />
              </oxd-grid-item>
            </oxd-grid>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.nickname"
                  :label="$t('pim.nickname')"
                  :rules="rules.nickname"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.employeeId"
                  :label="$t('general.employee_id')"
                  :rules="rules.employeeId"
                  :disabled="true"
                />
              </oxd-grid-item>
            </oxd-grid>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.drivingLicenseNo"
                  :label="$t('pim.driver_license_number')"
                  :rules="rules.drivingLicenseNo"
                />
              </oxd-grid-item>
            </oxd-grid>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item v-if="showSsnField">
                <oxd-input-field
                  v-model="employee.ssnNumber"
                  :label="$t('pim.ssn_number')"
                  :rules="rules.ssnNumber"
                  :disabled="!$can.update(`personal_sensitive_information`)"
                />
              </oxd-grid-item>
              <oxd-grid-item v-if="showSinField">
                <oxd-input-field
                  v-model="employee.sinNumber"
                  :label="$t('pim.sin_number')"
                  :rules="rules.sinNumber"
                  :disabled="!$can.update(`personal_sensitive_information`)"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.nationality"
                  type="select"
                  :label="$t('general.nationality')"
                  :rules="rules.nationality"
                  :clear="false"
                  :options="nationalities"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.maritalStatus"
                  type="select"
                  :label="$t('pim.marital_status')"
                  :rules="rules.maritalStatus"
                  :clear="false"
                  :options="maritalStatuses"
                />
              </oxd-grid-item>
            </oxd-grid>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.birthday"
                  type="date"
                  :rules="rules.birthday"
                  :label="$t('pim.date_of_birth')"
                  @keydown.enter.prevent
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.gender"
                  type="select"
                  :label="$t('pim.gender')"
                  :rules="rules.gender"
                  :clear="false"
                  :options="genders"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider v-if="showDeprecatedFields" />
          <oxd-form-row v-if="showDeprecatedFields">
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.militaryService"
                  :label="$t('pim.military_service')"
                  :rules="rules.militaryService"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="employee.smoker"
                  type="checkbox"
                  :label="$t('pim.smoker')"
                  :option-label="$t('general.yes')"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />
          <oxd-form-actions>
            <required-text />
            <submit-button />
          </oxd-form-actions>
        </oxd-form>
      </div>
    </div>
  </employee-data-layout>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
  validDateFormat,
} from '@ohrm/core/util/validation/rules';
import EmployeeDataLayout from './EmployeeDataLayout.vue';
import FullNameInput from '@/orangehrmPimPlugin/components/FullNameInput.vue';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {ref} from 'vue';
const employeeModel = {
  firstName: '',
  middleName: '',
  lastName: '',
  employeeId: '',
  otherId: '',
  drivingLicenseNo: '',
  ssnNumber: '',
  sinNumber: '',
  nationality: [],
  maritalStatus: [],
  birthday: '',
  gender: [],
  nickname: '',
  smoker: '',
  militaryService: '',
};

export default {
  components: {
    'employee-data-layout': EmployeeDataLayout,
    'full-name-input': FullNameInput,
  },

  props: {
    empNumber: {
      type: String,
      required: true,
    },
    nationalities: {
      type: Array,
      default: () => [],
    },
    showDeprecatedFields: {
      type: Boolean,
      default: false,
    },
    showSsnField: {
      type: Boolean,
      default: false,
    },
    showSinField: {
      type: Boolean,
      default: false,
    },
    hasNewContract: {
      type: Boolean,
      default: false,
    },
    hasUpdatedProfile: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const userHasUpdatedProfile = ref(!!props.hasUpdatedProfile);
    const userHasNewContract = ref(!!props.hasNewContract);

    if (userHasUpdatedProfile.value) {
      if (userHasNewContract.value) {
        navigate('/auth/signContract');
      } else {
        navigate('/dashboard/index');
      }
    }

    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/personal-details`,
    );

    return {
      http,
      userHasUpdatedProfile,
      userHasNewContract,
    };
  },

  data() {
    return {
      isLoading: false,
      isFormReady: false,
      employee: {...employeeModel},
      rawRules: {
        firstName: [required, shouldNotExceedCharLength(30)],
        middleName: [shouldNotExceedCharLength(30)],
        lastName: [required, shouldNotExceedCharLength(30)],
        employeeId: [shouldNotExceedCharLength(10)],
        otherId: [shouldNotExceedCharLength(30)],
        drivingLicenseNo: [required, shouldNotExceedCharLength(30)],
        nationality: [required],
        maritalStatus: [required],
        gender: [required],
        ssnNumber: [shouldNotExceedCharLength(30)],
        sinNumber: [shouldNotExceedCharLength(30)],
        nickname: [required, shouldNotExceedCharLength(30)],
        militaryService: [shouldNotExceedCharLength(30)],
        birthday: [required, validDateFormat('yyyy-MM-dd')],
      },

      maritalStatuses: [
        {id: 'Single', label: 'Single'},
        {id: 'Married', label: 'Married'},
        {id: 'Other', label: 'Other'},
      ],
      genders: [
        {id: '1', label: 'Male'},
        {id: '2', label: 'Female'},
      ],
    };
  },
  computed: {
    rules() {
      const wrap = (validator) => {
        return (v) => {
          if (!this.isFormReady) return true;
          return validator(v);
        };
      };

      const guardedRules = {};
      for (const field in this.rawRules) {
        guardedRules[field] = this.rawRules[field].map(wrap);
      }
      return guardedRules;
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .getAll()
      .then((response) => {
        this.updateModel(response);
        return this.http.request({method: 'GET', url: '/api/v2/pim/employees'});
      })
      .then((response) => {
        const {data} = response.data;
        this.rawRules.employeeId.push((v) => {
          if (!this.isFormReady) return true;
          const index = data.findIndex(
            (item) =>
              item.employeeId?.trim() &&
              String(item.employeeId).toLowerCase() === String(v).toLowerCase(),
          );
          if (index > -1) {
            const {empNumber} = data[index];
            return empNumber != this.empNumber
              ? this.$t('pim.employee_id_exists')
              : true;
          }
          return true;
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isFormReady = true;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            lastName: this.employee.lastName,
            firstName: this.employee.firstName,
            middleName: this.employee.middleName,
            employeeId: this.employee.employeeId,
            otherId: this.employee.otherId,
            drivingLicenseNo: this.employee.drivingLicenseNo,
            gender: this.employee.gender?.id,
            maritalStatus: this.employee.maritalStatus?.id,
            birthday: this.employee.birthday,
            nationalityId: this.employee.nationality?.id,
            ssnNumber: this.showSsnField ? this.employee.ssnNumber : undefined,
            sinNumber: this.showSinField ? this.employee.sinNumber : undefined,
            nickname: this.employee.nickname,
            smoker: this.showDeprecatedFields
              ? this.employee.smoker
              : undefined,
            militaryService: this.showDeprecatedFields
              ? this.employee.militaryService
              : undefined,
          },
        })
        .then((response) => {
          this.updateModel(response);
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.isLoading = false;
          if (this.userHasNewContract) {
            navigate('/auth/signContract');
          } else {
            navigate('/dashboard/index');
          }
        });
    },

    updateModel(response) {
      const {data} = response.data;
      Object.assign(this.employee, employeeModel, data);
      this.employee.maritalStatus = this.maritalStatuses.find(
        (item) => item.id === data.maritalStatus,
      );
      this.employee.nationality = this.nationalities.find(
        (item) => item.id === data.nationality?.id,
      );
      this.employee.gender = this.genders.find(
        (item) => item.id === data.gender.toString(),
      );
    },
  },
};
</script>

<style src="./employee-data.scss" lang="scss" scoped></style>
